import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { Socket } from 'socket.io-client';
import {
  getUnreadCount as fetchUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/api/notifications.api';
import { createNotificationsSocket } from '@/lib/notificationsSocket';
import { showErrorToast, showInfoToast } from '@/lib/toast';
import { mergeNotifications } from '@/notifications/mergeNotifications';
import { composeNotificationText, getNotificationLink } from '@/notifications/notificationMessages';
import {
  NOTIFICATION_WS_EVENTS,
  type NotificationItem,
  type NotificationNewEvent,
  type NotificationUnreadCountEvent,
  type PageMeta,
} from '@/types/shared';

export type NotificationsFetchStatus = 'loading' | 'loaded' | 'error';

const PAGE_SIZE = 20;

interface NotificationsContextValue {
  items: NotificationItem[];
  unreadCount: number;
  status: NotificationsFetchStatus;
  hasMore: boolean;
  loadingMore: boolean;
  loadMoreError: boolean;
  /** Bumped with a fresh (unique) value on every live `notification:new` arrival — a badge-pulse
   * or list-entrance animation can key off this to re-trigger exactly once per arrival, never on
   * the initial mount (16-notifications-ux.md §1.2/§4.4). `null` until the first arrival. */
  arrivalPulse: number | null;
  /** Plain-text, screen-reader-facing "New notification: <message>" string for the always-mounted
   * `aria-live="polite"` region (§6) — updated once per arrival regardless of panel open state. */
  announcement: string;
  /** Whether the bell's panel is currently open — informs the toast-on-arrival suppression rule
   * (§4.4: no toast while the user is already looking at the row landing in the open panel). */
  isPanelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  fetchInitial: () => void;
  loadMore: () => void;
  markRead: (id: string) => Promise<void>;
  /** Throws on failure so the panel can render its own inline error text (§2.2) — not optimistic. */
  markAllRead: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

/**
 * Notifications data/socket provider (T115). Mounted once per authenticated session — see
 * `AppShell.tsx`, which only renders while `ProtectedRoute` has already confirmed
 * `status === 'authenticated'`, so this provider mounting/unmounting is a reliable proxy for
 * login/logout: the socket is created on mount and torn down on unmount (logout, or the whole
 * shell unmounting for any other reason), never leaking a connection past the session.
 */
export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [status, setStatus] = useState<NotificationsFetchStatus>('loading');
  const [meta, setMeta] = useState<PageMeta | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [arrivalPulse, setArrivalPulse] = useState<number | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [isPanelOpen, setIsPanelOpenState] = useState(false);

  // Refs mirroring the latest render's fast-changing values, read from inside the long-lived socket
  // effect below (whose own deps are `[]` so the socket is created/torn down exactly once per
  // provider lifetime — it must not reconnect just because the language toggled or the panel
  // opened/closed).
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const isPanelOpenRef = useRef(isPanelOpen);
  isPanelOpenRef.current = isPanelOpen;
  const setPanelOpen = useCallback((open: boolean) => {
    isPanelOpenRef.current = open;
    setIsPanelOpenState(open);
  }, []);

  const fetchInitial = useCallback(() => {
    setStatus('loading');
    listNotifications({ page: 1, pageSize: PAGE_SIZE })
      .then((res) => {
        setItems((prev) => mergeNotifications(prev, res.data));
        setUnreadCount(res.unreadCount);
        setMeta(res.meta);
        setStatus('loaded');
      })
      .catch(() => {
        setStatus('error');
      });
  }, []);

  useEffect(() => {
    fetchInitial();
  }, [fetchInitial]);

  const loadMore = useCallback(() => {
    setMeta((currentMeta) => {
      if (!currentMeta || currentMeta.page >= currentMeta.totalPages || loadingMore) {
        return currentMeta;
      }
      const nextPage = currentMeta.page + 1;
      setLoadingMore(true);
      setLoadMoreError(false);
      listNotifications({ page: nextPage, pageSize: PAGE_SIZE })
        .then((res) => {
          setItems((prev) => mergeNotifications(prev, res.data));
          setUnreadCount(res.unreadCount);
          setMeta(res.meta);
        })
        .catch(() => {
          setLoadMoreError(true);
        })
        .finally(() => {
          setLoadingMore(false);
        });
      return currentMeta;
    });
  }, [loadingMore]);

  const markRead = useCallback(
    async (id: string) => {
      const target = itemsRef.current.find((n) => n.id === id);
      if (!target || target.readAt) return;

      const readAt = new Date().toISOString();
      // Optimistic — 16-notifications-ux.md §2.6: dot/tint/badge update immediately, before the
      // request resolves, then navigation fires (navigation is the caller's job, not this method's).
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, readAt } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));

      try {
        const res = await markNotificationRead(id);
        setItems((prev) => prev.map((n) => (n.id === id ? res.notification : n)));
        setUnreadCount(res.unreadCount);
      } catch {
        // Revert + surface as a toast — by the time this fails the user has likely already
        // navigated away (§2.6), so an inline panel message would go unseen.
        setItems((prev) => prev.map((n) => (n.id === id ? { ...n, readAt: null } : n)));
        setUnreadCount((prev) => prev + 1);
        showErrorToast(t('toast.genericError'));
      }
    },
    [t],
  );

  const markAllRead = useCallback(async () => {
    // Not optimistic (§2.6) — throws on failure so `NotificationsPanel` can show its own inline
    // error text under the header; unread state is left untouched here on failure.
    const res = await markAllNotificationsRead();
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt: now })));
    setUnreadCount(res.unreadCount);
  }, []);

  // Latest-value ref read from inside the one-time socket effect below, so that effect's deps can
  // stay `[]` (a genuine one-per-mount subscription) without capturing stale closures over `t`
  // (changes on language toggle), `navigate` (stable, but kept here for symmetry), or `markRead`.
  const latestRef = useRef({ t, navigate, markRead });
  latestRef.current = { t, navigate, markRead };

  // Socket lifecycle — created once per provider mount (i.e. once per authenticated session),
  // connected immediately, and disconnected on unmount. `handleConnect` does the mandatory
  // reconnect-backfill (T111 §4): every (re)connection reloads unread notifications via REST so
  // nothing pushed while offline/disconnected is ever lost — the WS push is a latency optimization
  // on top of this, never the delivery guarantee itself (D6).
  useEffect(() => {
    const socket: Socket = createNotificationsSocket();

    function backfill() {
      listNotifications({ unreadOnly: true })
        .then((res) => {
          setItems((prev) => mergeNotifications(prev, res.data));
          setUnreadCount(res.unreadCount);
        })
        .catch(() => {
          // Silent — the reconnect-backfill is a background reconciliation; the initial REST
          // fetch (`fetchInitial`) already owns the panel's loading/error surface. A missed
          // backfill self-corrects on the next successful (re)connect.
        });
    }

    function handleNew(event: NotificationNewEvent) {
      setItems((prev) => mergeNotifications(prev, [event.notification]));
      setUnreadCount(event.unreadCount);
      setArrivalPulse(Date.now());

      const {
        t: currentT,
        navigate: currentNavigate,
        markRead: currentMarkRead,
      } = latestRef.current;
      const messageText = composeNotificationText(currentT, event.notification);
      setAnnouncement(`${currentT('notifications.newArrivalAnnouncementPrefix')}${messageText}`);

      // Optional (16-notifications-ux.md §4.4, flagged for BA/PM sign-off, not required by the
      // task's DoD): a low-friction info toast, only while the panel is closed (if it's open the
      // user is already looking at the new row landing at the top — a toast would be redundant).
      if (!isPanelOpenRef.current) {
        const link = getNotificationLink(event.notification);
        showInfoToast(messageText, {
          onClick: () => {
            void currentMarkRead(event.notification.id);
            if (link) currentNavigate(link);
          },
        });
      }
    }

    function handleUnreadCount(event: NotificationUnreadCountEvent) {
      setUnreadCount(event.unreadCount);
    }

    socket.on('connect', backfill);
    socket.on(NOTIFICATION_WS_EVENTS.new, handleNew);
    socket.on(NOTIFICATION_WS_EVENTS.unreadCount, handleUnreadCount);
    socket.connect();

    return () => {
      socket.off('connect', backfill);
      socket.off(NOTIFICATION_WS_EVENTS.new, handleNew);
      socket.off(NOTIFICATION_WS_EVENTS.unreadCount, handleUnreadCount);
      socket.disconnect();
    };
    // Deliberately `[]` — a one-time-per-mount subscription. Fast-changing values (t, navigate,
    // markRead, panel-open) are read via the refs above rather than listed as deps, so the socket
    // never reconnects just because the language toggled or the panel opened/closed.
  }, []);

  const hasMore = Boolean(meta && meta.page < meta.totalPages);

  const value = useMemo<NotificationsContextValue>(
    () => ({
      items,
      unreadCount,
      status,
      hasMore,
      loadingMore,
      loadMoreError,
      arrivalPulse,
      announcement,
      isPanelOpen,
      setPanelOpen,
      fetchInitial,
      loadMore,
      markRead,
      markAllRead,
    }),
    [
      items,
      unreadCount,
      status,
      hasMore,
      loadingMore,
      loadMoreError,
      arrivalPulse,
      announcement,
      isPanelOpen,
      setPanelOpen,
      fetchInitial,
      loadMore,
      markRead,
      markAllRead,
    ],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return ctx;
}

/** Exposed for a future/optional standalone re-sync (e.g. a "focus" listener) — not wired this
 * pass since the list/mark responses already carry `unreadCount` inline (D5). */
export { fetchUnreadCount };
